<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contacts: {
    type: Array,
    default: () => [],
  },
  query: {
    type: String,
    default: '',
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  isCreatingContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'back',
  'append',
  'delete',
  'search',
  'selectContact',
  'continue',
]);

const { t } = useI18n();

const keypadButtons = [
  { key: '1', subLabel: '' },
  { key: '2', subLabel: 'ABC' },
  { key: '3', subLabel: 'DEF' },
  { key: '4', subLabel: 'GHI' },
  { key: '5', subLabel: 'JKL' },
  { key: '6', subLabel: 'MNO' },
  { key: '7', subLabel: 'PQRS' },
  { key: '8', subLabel: 'TUV' },
  { key: '9', subLabel: 'WXYZ' },
];

const plusSymbol = '+';
const zeroDigit = '0';

const digitCount = computed(() => props.query.replace(/\D/g, '').length);
const normalizedQuery = computed(() => props.query.replace(/\s+/g, ''));

const shouldShowInstruction = computed(() => !normalizedQuery.value);

const emptyStateLabel = computed(() => {
  if (props.isSearching) {
    return t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.SEARCHING');
  }
  if (digitCount.value < 2) {
    return t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.MIN_DIGITS');
  }
  if (!props.contacts.length) {
    return t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.NO_RESULTS');
  }
  return '';
});

const handleInput = event => {
  emit('search', event.target.value);
};
</script>

<template>
  <div
    class="acl-phone-dialer absolute inset-0 z-10 flex flex-col bg-white dark:bg-n-solid-1"
  >
    <div
      class="acl-phone-dialer__header px-4 pt-4 pb-2 flex-shrink-0 border-b border-n-weak/50"
    >
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="acl-phone-dialer__back-btn"
          :aria-label="t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.BACK')"
          @click="emit('back')"
        >
          <span class="i-lucide-arrow-left size-5" />
        </button>
        <h3 class="acl-phone-dialer__title">
          {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.TITLE') }}
        </h3>
      </div>
    </div>

    <div
      class="acl-phone-dialer__body flex-1 min-h-0 overflow-y-auto px-4 pb-5"
    >
      <div class="pt-4">
        <label class="acl-phone-dialer__label block mb-2">
          {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.LABEL') }}
        </label>
        <div class="acl-phone-dialer__input-wrap">
          <input
            type="tel"
            inputmode="tel"
            :value="query"
            :placeholder="
              t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.PLACEHOLDER')
            "
            class="acl-phone-dialer__input"
            @input="handleInput"
          />
        </div>
      </div>

      <div class="acl-phone-dialer__spacer">
        <p v-if="shouldShowInstruction" class="acl-phone-dialer__instruction">
          {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.EMPTY_STATE') }}
        </p>
      </div>

      <div class="acl-phone-dialer__keypad">
        <button
          v-for="button in keypadButtons"
          :key="button.key"
          type="button"
          class="acl-phone-dialer__key"
          @click="emit('append', button.key)"
        >
          <span class="acl-phone-dialer__key-number">{{ button.key }}</span>
          <span v-if="button.subLabel" class="acl-phone-dialer__key-subtitle">
            {{ button.subLabel }}
          </span>
        </button>

        <button
          type="button"
          class="acl-phone-dialer__key acl-phone-dialer__key--plus"
          @click="emit('append', plusSymbol)"
        >
          <span class="acl-phone-dialer__key-number">{{ plusSymbol }}</span>
        </button>

        <button
          type="button"
          class="acl-phone-dialer__key acl-phone-dialer__key--zero"
          @click="emit('append', zeroDigit)"
        >
          <span class="acl-phone-dialer__key-number">{{ zeroDigit }}</span>
        </button>

        <button
          type="button"
          class="acl-phone-dialer__delete"
          :aria-label="t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.DELETE')"
          :disabled="digitCount === 0"
          @click="emit('delete')"
        >
          <span class="i-lucide-delete size-6" />
        </button>
      </div>

      <div class="acl-phone-dialer__actions">
        <button
          type="button"
          class="acl-phone-dialer__continue"
          :disabled="digitCount === 0 || isCreatingContact"
          @click="emit('continue')"
        >
          {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.CONTINUE') }}
        </button>
      </div>

      <div class="acl-phone-dialer__contacts mt-5">
        <p class="acl-phone-dialer__section-title">
          {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.CONTACTS_FOUND') }}
        </p>
        <div v-if="emptyStateLabel" class="acl-phone-dialer__empty-results">
          {{ emptyStateLabel }}
        </div>
        <ul v-else class="acl-phone-dialer__contacts-list">
          <li
            v-for="contact in contacts"
            :key="contact.id"
            class="acl-phone-dialer__contact-item"
          >
            <button
              type="button"
              class="acl-phone-dialer__contact-button"
              @click="emit('selectContact', contact)"
            >
              <span class="min-w-0">
                <span class="acl-phone-dialer__contact-name">
                  {{ contact.name }}
                </span>
                <span class="acl-phone-dialer__contact-phone">
                  {{ contact.phoneNumber || contact.email }}
                </span>
              </span>
              <span class="acl-phone-dialer__contact-open">
                {{ t('COMPOSE_NEW_CONVERSATION.PHONE_DIALER.SELECT') }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.acl-phone-dialer__back-btn {
  @apply inline-flex h-9 w-9 items-center justify-center border-0 bg-transparent p-0 text-n-slate-12 transition-none;
}

.acl-phone-dialer__title {
  @apply flex-1 min-w-0 text-xl font-semibold text-n-slate-12 text-left;
}

.acl-phone-dialer__label {
  @apply text-[11px] font-semibold uppercase tracking-[0.08em] text-n-slate-10;
}

.acl-phone-dialer__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 14px !important;
  background: rgba(194, 189, 184, 0.15);
  border: none;
  border-radius: 50px;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}

.acl-phone-dialer__input-wrap:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.acl-phone-dialer__input-wrap:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.acl-phone-dialer__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  width: 100%;
  border: none !important;
  background: transparent !important;
  color: #1f2937;
  font-size: 13px;
  line-height: 40px;
  font-weight: 400;
  padding: 0;
  margin: 0;
  outline: none !important;
  box-shadow: none !important;
}

.acl-phone-dialer__input::placeholder {
  color: #8a8a8a;
}

.acl-phone-dialer__input:focus,
.acl-phone-dialer__input:focus-visible,
.acl-phone-dialer__input:focus-within {
  border: none !important;
  background: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}

.acl-phone-dialer__spacer {
  @apply flex flex-none items-center justify-center px-3 py-3;
  min-height: 72px;
}

.acl-phone-dialer__instruction {
  @apply max-w-[18rem] text-center text-sm text-n-slate-8;
}

.acl-phone-dialer__keypad {
  @apply mt-auto grid grid-cols-3 gap-y-1;
}

.acl-phone-dialer__key,
.acl-phone-dialer__delete {
  @apply flex min-h-[4.25rem] w-full flex-col items-center justify-center border-0 bg-transparent px-0 text-n-slate-12 transition-colors;
}

.acl-phone-dialer__key:hover,
.acl-phone-dialer__key:focus-visible,
.acl-phone-dialer__delete:hover,
.acl-phone-dialer__delete:focus-visible {
  background: rgba(0, 0, 0, 0.06);
}

.acl-phone-dialer__key-number {
  @apply text-[1.5rem] font-medium leading-none tracking-tight;
}

.acl-phone-dialer__key-subtitle {
  @apply mt-1 text-[0.65rem] font-medium tracking-[0.18em] text-n-slate-8;
}

.acl-phone-dialer__key--plus,
.acl-phone-dialer__key--zero,
.acl-phone-dialer__delete {
  @apply min-h-[3.5rem];
}

.acl-phone-dialer__delete {
  @apply items-center justify-center disabled:cursor-not-allowed disabled:opacity-40;
}

.acl-phone-dialer__actions {
  @apply mt-4;
}

.acl-phone-dialer__continue {
  @apply h-11 w-full border-0 bg-transparent px-0 text-sm font-medium text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40;
}

.acl-phone-dialer__section-title {
  @apply mb-3 text-sm font-medium text-n-slate-12;
}

.acl-phone-dialer__empty-results {
  @apply py-4 text-center text-xs text-n-slate-9;
}

.acl-phone-dialer__contacts-list {
  @apply flex max-h-[180px] flex-col divide-y divide-n-weak/60 overflow-y-auto pr-1;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.acl-phone-dialer__contact-item {
  @apply py-2;
}

.acl-phone-dialer__contact-button {
  @apply flex w-full items-center justify-between gap-3 px-0 text-left;
}

.acl-phone-dialer__contact-name {
  @apply block truncate text-sm font-medium text-n-slate-12;
}

.acl-phone-dialer__contact-phone {
  @apply block truncate text-xs text-n-slate-9;
}

.acl-phone-dialer__contact-open {
  @apply text-xs font-medium text-n-slate-8;
}

:global(.dark) .acl-phone-dialer__back-btn,
:global(.dark) .acl-phone-dialer__title,
:global(.dark) .acl-phone-dialer__input,
:global(.dark) .acl-phone-dialer__key,
:global(.dark) .acl-phone-dialer__delete,
:global(.dark) .acl-phone-dialer__section-title,
:global(.dark) .acl-phone-dialer__contact-name {
  color: #f3f4f6;
}

:global(.dark) .acl-phone-dialer__input-wrap {
  background: rgba(255, 255, 255, 0.04);
}

:global(.dark) .acl-phone-dialer__input-wrap:hover {
  box-shadow: inset 0 0 0 1px #4b5563;
}

:global(.dark) .acl-phone-dialer__input-wrap:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

:global(.dark) .acl-phone-dialer__input::placeholder,
:global(.dark) .acl-phone-dialer__instruction,
:global(.dark) .acl-phone-dialer__key-subtitle,
:global(.dark) .acl-phone-dialer__empty-results,
:global(.dark) .acl-phone-dialer__contact-phone,
:global(.dark) .acl-phone-dialer__contact-open {
  color: #9ca3af;
}

:global(.dark) .acl-phone-dialer__input {
  color: #f3f4f6;
}

:global(.dark) .acl-phone-dialer__key:hover,
:global(.dark) .acl-phone-dialer__key:focus-visible,
:global(.dark) .acl-phone-dialer__delete:hover,
:global(.dark) .acl-phone-dialer__delete:focus-visible {
  background: rgba(255, 255, 255, 0.06);
}
</style>
